"use client";

import { Search as SearchIcon } from "@faire/slate-icons/Search";
import { componentCls } from "@faire/slate/lib/tracking";
import { InputBase } from "@faire/slate/private/form/InputBase";
import { useControllableState } from "@faire/slate/private/hooks/useControllableState";
import { useMountStateCls } from "@faire/slate/private/hooks/useMountStateCls";
import { ClearButton } from "@faire/slate/private/search/ClearButton";
import { useCollapseWidthFreeze } from "@faire/slate/private/search/hooks/useCollapseWidthFreeze";
import type { ISearchWebProps } from "@faire/slate/private/search/types";
import React, { useCallback, useImperativeHandle, useRef } from "react";

type IProps = {
  /**
   * When `true`, the search condenses to a circular icon when unfocused.
   * Focusing expands it to the full-width field.
   *
   * @default false
   */
  collapsed?: boolean;

  /**
   * Whether the text input component is disabled.
   */
  disabled?: boolean;

  /**
   * The function to call when the clear button is clicked.
   * If defined, the clear button will be shown when the input has a value.
   */
  onClear?: () => void;

  /**
   * The placeholder of the text input component.
   */
  placeholder?: string;

  /**
   * The value of the text input component.
   */
  value?: string;

  /**
   * When `true`, opts this search input into PostHog session replay
   * unmasking so typed search terms are recorded instead of masked. Only
   * use for inputs that never hold PII.
   *
   * @default false
   */
  posthogUnmask?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISearchWebProps;

/**
 * Search Inputs allow users to quickly and easily find what they are looking for.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Search-2722efb5c25a80cb89b5da0fbb2cd117 | Slate/Search}
 */
export const Search: React.FC<IProps> = ({
  collapsed = false,
  disabled,
  onClear,
  posthogUnmask = false,
  ref,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentValue, setCurrentValue] = useControllableState({
    value,
    initialValue: typeof defaultValue === "string" ? defaultValue : "",
  });
  const hasValue = !!currentValue;
  const showClearSuffix = onClear && !disabled;

  // Suppress the collapse/expand transition for the first committed state so
  // the Search never animates on initial mount — including when a parent
  // resolves `collapsed` in a layout effect (e.g. TableToolbar), which flips
  // the prop before the first paint.
  const transitionCls = useMountStateCls({
    false: "fs-search--no-transition",
  });

  // Keeps the collapse animation smooth in flexible layouts: the expanded
  // Search is `width: 100%`, so without this it would snap to the full row
  // width at the start of the collapse before shrinking. See the hook for
  // details.
  const {
    containerRef,
    onFocus: freezeOnFocus,
    onBlur: freezeOnBlur,
  } = useCollapseWidthFreeze(collapsed);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      freezeOnFocus();
      onFocus?.(e);
    },
    [freezeOnFocus, onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      freezeOnBlur();
      onBlur?.(e);
    },
    [freezeOnBlur, onBlur]
  );

  const onContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const onContainerMouseDown = useCallback((e: React.MouseEvent) => {
    // Keep focus on the input when pressing the background so the collapsed
    // search does not flicker (collapse + re-expand). The input keeps its own
    // default behavior so cursor placement / text selection still works.
    if (e.target !== inputRef.current) {
      e.preventDefault();
    }
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      onChange?.(e);
    },
    [setCurrentValue, onChange]
  );

  const onClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCurrentValue("");
      onClear?.();
      inputRef.current?.focus();
    },
    [onClear, setCurrentValue]
  );

  useImperativeHandle(ref, () => inputRef.current!);

  return (
    <InputBase
      className={componentCls(
        "fs-search",
        collapsed ? "fs-search--collapsed" : undefined,
        hasValue ? "fs-search--has-value" : undefined,
        transitionCls
      )}
      containerRef={containerRef}
      data-component-name="Slate/Search"
      disabled={disabled}
      unmaskPosthog={posthogUnmask}
      onBlur={handleBlur}
      onContainerClick={onContainerClick}
      onContainerMouseDown={onContainerMouseDown}
      onFocus={handleFocus}
      prefix={
        <span className="fs-search__icon">
          <SearchIcon className="text-base" titleAccess="" color="inherit" />
        </span>
      }
      ref={inputRef}
      suffix={
        showClearSuffix ? (
          <span className="fs-search__btn-clear" hidden={!hasValue}>
            <ClearButton onClick={onClearClick} />
          </span>
        ) : null
      }
      type="search"
      value={currentValue}
      onChange={handleChange}
      {...props}
    />
  );
};
